import {createContext, useContext} from 'react'
import {QueryClient, useQuery} from '@tanstack/react-query'

const qc = new QueryClient()
const appConfigQueryKey = ['app-config']

/**
 * Matches the types defined in our `app-config` worker
 */
type AppConfigResponse = {
  liveNow: {
    allow: string[]
    exceptions: {
      did: string
      allow: string[]
    }[]
  }
}

export const DEFAULT_APP_CONFIG_RESPONSE: AppConfigResponse = {
  liveNow: {
    allow: [],
    exceptions: [],
  },
}

function fetchAppConfig(): Promise<AppConfigResponse | null> {
  // bateclub does not run Bluesky's app-config worker, and that worker is
  // CORS-locked to bsky.app origins, so fetching it from bateclub.app only
  // produced console errors before falling back to this default. Skip the
  // network call and use the built-in default (its only field is the Live Now
  // allowlist, which bateclub does not use). To re-enable, restore the fetch of
  // `${APP_CONFIG_URL}/config` and point APP_CONFIG_URL at an endpoint that
  // allows this origin.
  return Promise.resolve(DEFAULT_APP_CONFIG_RESPONSE)
}

const Context = createContext<AppConfigResponse>(DEFAULT_APP_CONFIG_RESPONSE)

export function Provider({children}: React.PropsWithChildren<{}>) {
  const {data} = useQuery<AppConfigResponse | null>(
    {
      staleTime: Infinity,
      queryKey: appConfigQueryKey,
      refetchInterval: query => {
        // refetch regularly if fetch failed, otherwise never refetch
        return query.state.status === 'error' ? 60e3 : Infinity
      },
      async queryFn() {
        return fetchAppConfig()
      },
    },
    qc,
  )
  return (
    <Context.Provider value={data ?? DEFAULT_APP_CONFIG_RESPONSE}>
      {children}
    </Context.Provider>
  )
}

export async function prefetchAppConfig() {
  try {
    const data = await fetchAppConfig()
    if (data) {
      qc.setQueryData(appConfigQueryKey, data)
    }
  } catch {}
}

export function useAppConfig() {
  const ctx = useContext(Context)
  if (!ctx) {
    throw new Error('useAppConfig must be used within a Provider')
  }
  return ctx
}
